import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { applyCmsContent, getDefaultCmsContent, normalizeCmsContent } from "@/cms/cmsModel";

const CmsContentContext = createContext(null);

export const CmsContentProvider = ({ children }) => {
  const [document, setDocument] = useState({ revision: 0, updatedAt: null, data: getDefaultCmsContent() });
  const [locale, setLocaleState] = useState(() => {
    try { return window.localStorage.getItem("az-language") === "ar" ? "ar" : "de"; }
    catch { return "de"; }
  });
  const [runtimeKey, setRuntimeKey] = useState(0);

  const setLocale = (nextLocale) => setLocaleState(nextLocale === "ar" ? "ar" : "de");

  useEffect(() => {
    try { window.localStorage.setItem("az-language", locale); } catch { /* Optional preference only. */ }
    window.document.documentElement.lang = locale;
    window.document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    window.document.title = locale === "ar"
      ? "AZ Gruppe – شريككم من الألف إلى الياء"
      : "AZ Gruppe – Ihr Partner von A bis Z";
  }, [locale]);

  useEffect(() => {
    let active = true;
    fetch("/api/cms/content", { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("CMS unavailable"))))
      .then((remote) => {
        if (!active) return;
        const data = normalizeCmsContent(remote?.data && Object.keys(remote.data).length ? remote.data : {});
        setDocument({ ...remote, data });
      })
      .catch(() => {
        setDocument((current) => ({ ...current, data: getDefaultCmsContent() }));
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    applyCmsContent(document.data, locale);
    setRuntimeKey((value) => value + 1);
  }, [document.data, locale]);

  const value = useMemo(
    () => ({ document, setDocument, locale, setLocale, isRtl: locale === "ar", runtimeKey }),
    [document, locale, runtimeKey]
  );
  return (
    <CmsContentContext.Provider value={value}>
      <div>{children}</div>
    </CmsContentContext.Provider>
  );
};

export const useCmsDocument = () => {
  const value = useContext(CmsContentContext);
  if (!value) throw new Error("useCmsDocument must be used inside CmsContentProvider");
  return value;
};
